import { differenceInCalendarDays, format, startOfToday } from 'date-fns';
import { ScheduledSession } from 'api/lotusBlossom/lotusBlossomAPI';

/**
 * Sessions have no table of their own — each is a CANmail list created by the
 * Stripe fulfillment when someone enrolls, with the date in the list name:
 *
 *   Quantum Healing Theme [2026-04-22]: Heart Chakra — Love & Connection
 *
 * Undated lists are skipped, since they can't be placed on a calendar: the
 * "Quantum Healing Theme: <name>" fallback the fulfillment uses when a checkout
 * carried no themeStartDate, and "Quantum Healing Subscribers" itself.
 *
 * The fulfillment truncates names to 100 chars, clipping only the trailing
 * theme text — the date sits near the front, so parsing is unaffected.
 */
const THEME_LIST_PATTERN =
	/^\s*Quantum Healing Theme\s*\[\s*(\d{4})-(\d{2})-(\d{2})\s*\]\s*:\s*(.+?)\s*$/i;

export interface MailListRow {
	/** The legacy /api2.0 endpoint returns this as a string, so callers coerce. */
	list_id: number | string;
	list_name: string;
}

/** Ids arrive as strings; normalise once so `===` comparisons behave. */
function toListId(value: number | string): number | null {
	const id = Number(value);
	return Number.isFinite(id) ? id : null;
}

// Mirrors SUBSCRIBERS_LIST_NAME in the backend's lotusBlossom.types.ts — rename
// in both places or the roster loses every member.
export const SUBSCRIBERS_LIST_NAME = 'Quantum Healing Subscribers';

// Case- and whitespace-insensitive, matching the backend's findListByNameLenient
// so a stray double space in the stored name doesn't lose every member.
export function findListByName(rows: MailListRow[], name: string): MailListRow | null {
	if (!Array.isArray(rows)) return null;

	const normalize = (s: string) => s.replace(/\s+/g, '').toLowerCase();
	const target = normalize(name);

	return rows.find((r) => r?.list_name && normalize(r.list_name) === target) ?? null;
}

export function findSubscribersList(rows: MailListRow[]): MailListRow | null {
	return findListByName(rows, SUBSCRIBERS_LIST_NAME);
}

/** list_id of the subscribers list, coerced, or null if it doesn't exist yet. */
export function findSubscribersListId(rows: MailListRow[]): number | null {
	const row = findSubscribersList(rows);
	return row ? toListId(row.list_id) : null;
}

export const PARTICIPANTS_LIST_NAME = 'Quantum Healing Participants';

/** list_id of the participants list, or null if it doesn't exist. */
export function findParticipantsListId(rows: MailListRow[]): number | null {
	const row = findListByName(rows, PARTICIPANTS_LIST_NAME);
	return row ? toListId(row.list_id) : null;
}

export interface ThemeSession {
	listId: number;
	listName: string;
	/** The theme text after the colon, e.g. "Heart Chakra — Love & Connection". */
	themeName: string;
	/** Local midnight on the session's first day. */
	date: Date;
	/** The raw "YYYY-MM-DD" as it appeared in the list name. */
	dateISO: string;
	/**
	 * Local midnight on the session's last day. The list name doesn't carry it,
	 * so it is the first day until withEndDates fills it in from the schedule.
	 */
	endDate: Date;
}

/**
 * Parse one mail list into a session, or null if its name isn't a dated theme
 * list. Uses the local-time Date constructor, not `new Date('2026-04-22')` —
 * that ISO-parses to UTC midnight and lands a day early in Edmonton, shifting
 * every countdown.
 */
export function parseThemeList(row: MailListRow): ThemeSession | null {
	if (!row?.list_name) return null;

	const match = THEME_LIST_PATTERN.exec(row.list_name);
	if (!match) return null;

	const [, yearStr, monthStr, dayStr, themeName] = match;
	const year = Number(yearStr);
	const month = Number(monthStr);
	const day = Number(dayStr);

	const date = new Date(year, month - 1, day);

	// Reject dates that don't exist (e.g. [2026-02-31], which would silently
	// roll forward into March).
	if (
		date.getFullYear() !== year ||
		date.getMonth() !== month - 1 ||
		date.getDate() !== day
	) {
		return null;
	}

	const listId = toListId(row.list_id);
	if (listId === null) return null;

	return {
		listId,
		listName: row.list_name,
		themeName,
		date,
		dateISO: `${yearStr}-${monthStr}-${dayStr}`,
		endDate: date,
	};
}

/** Every dated theme list, earliest first. Non-session lists are dropped. */
export function parseThemeLists(rows: MailListRow[]): ThemeSession[] {
	if (!Array.isArray(rows)) return [];
	return rows
		.map(parseThemeList)
		.filter((s): s is ThemeSession => s !== null)
		.sort((a, b) => a.date.getTime() - b.date.getTime());
}

const normalizeListName = (name: string) => name.replace(/\s+/g, '').toLowerCase();

/**
 * The name a scheduled session's list goes by. Mirrors the backend's
 * themeListName, including its 100-character cap — two spellings of one
 * session would never match.
 */
function scheduledListName(session: ScheduledSession): string {
	return `Quantum Healing Theme [${session.startDate}]: ${session.title}`.slice(0, 100);
}

/** Local midnight on a "YYYY-MM-DD" day, or null if it isn't a real one. */
function localDay(iso: string): Date | null {
	const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso ?? '');
	if (!match) return null;
	const [year, month, day] = match.slice(1).map(Number);
	const date = new Date(year, month - 1, day);
	return date.getFullYear() === year && date.getMonth() === month - 1 && date.getDate() === day
		? date
		: null;
}

/**
 * Gives each session its last day from the published schedule, matched on the
 * list name the backend gave it. A list the schedule doesn't know — a legacy
 * or hand-made one — keeps its first day as its last, so it is treated as a
 * one-day session.
 */
export function withEndDates(
	sessions: ThemeSession[],
	scheduled: ScheduledSession[]
): ThemeSession[] {
	const endByName = new Map<string, Date>();
	scheduled.forEach((s) => {
		const end = localDay(s.endDate);
		if (end) endByName.set(normalizeListName(scheduledListName(s)), end);
	});

	return sessions.map((session) => {
		const end = endByName.get(normalizeListName(session.listName));
		return end && end >= session.date ? { ...session, endDate: end } : session;
	});
}

/**
 * Sessions running on `today`: started, and not past their last day. A six-day
 * session stays current for all six days.
 */
export function findCurrentSessions(
	sessions: ThemeSession[],
	today: Date = startOfToday()
): ThemeSession[] {
	return sessions.filter(
		(s) =>
			differenceInCalendarDays(s.date, today) <= 0 &&
			differenceInCalendarDays(s.endDate, today) >= 0
	);
}

/** Sessions that haven't started yet, soonest first. */
export function findUpcomingSessions(
	sessions: ThemeSession[],
	today: Date = startOfToday()
): ThemeSession[] {
	return sessions.filter((s) => differenceInCalendarDays(s.date, today) > 0);
}

/**
 * Sessions whose last day is before `today`, most recent first. A running
 * session is excluded so it can't appear under both the next and past views.
 */
export function findPastSessions(
	sessions: ThemeSession[],
	today: Date = startOfToday()
): ThemeSession[] {
	return sessions
		.filter((s) => differenceInCalendarDays(s.endDate, today) < 0)
		.sort((a, b) => b.date.getTime() - a.date.getTime());
}

/** Whole days from `today` to the session. 0 = today, 1 = tomorrow. */
export function daysUntil(
	session: ThemeSession,
	today: Date = startOfToday()
): number {
	return differenceInCalendarDays(session.date, today);
}

/** e.g. "Wednesday, April 22, 2026" */
export function formatSessionDate(session: ThemeSession): string {
	return format(session.date, 'EEEE, MMMM d, yyyy');
}

/**
 * The header sentence as {lead}{emphasis}{connector}{dateLabel}, split so the
 * caller can bold the middle parts without markup living down here. "in 0 days"
 * and "in 1 days" read badly, so those two cases use a word and a comma.
 */
export interface NextSessionCopy {
	lead: string;
	emphasis: string;
	connector: string;
	dateLabel: string;
}

export function buildNextSessionCopy(
	session: ThemeSession,
	today: Date = startOfToday()
): NextSessionCopy {
	const days = daysUntil(session, today);
	const dateLabel = formatSessionDate(session);
	const lead = 'Your next quantum healing session is';

	if (days === 0) {
		return { lead: `${lead} `, emphasis: 'today', connector: ', ', dateLabel };
	}
	if (days === 1) {
		return { lead: `${lead} `, emphasis: 'tomorrow', connector: ', ', dateLabel };
	}
	return {
		lead: `${lead} in `,
		emphasis: `${days} days`,
		connector: ' on ',
		dateLabel,
	};
}

/**
 * The same sentence shape for a session already running, e.g. "Your current
 * quantum healing session is **happening now** — day 3 of 6, ending **Monday,
 * October 26, 2026**".
 */
export function buildCurrentSessionCopy(
	session: ThemeSession,
	today: Date = startOfToday()
): NextSessionCopy {
	const lead = 'Your current quantum healing session is ';
	const totalDays = differenceInCalendarDays(session.endDate, session.date) + 1;
	const day = differenceInCalendarDays(today, session.date) + 1;

	if (totalDays <= 1) {
		return {
			lead,
			emphasis: 'happening today',
			connector: ', ',
			dateLabel: formatSessionDate(session),
		};
	}
	return {
		lead,
		emphasis: 'happening now',
		connector: ` — day ${day} of ${totalDays}, ending `,
		dateLabel: format(session.endDate, 'EEEE, MMMM d, yyyy'),
	};
}

/** e.g. "Oct 21 – 26, 2026", "Oct 28 – Nov 2, 2026", or "Oct 21, 2026" for one day. */
export function formatSessionRange(session: ThemeSession): string {
	const { date: start, endDate: end } = session;
	if (differenceInCalendarDays(end, start) <= 0) return format(start, 'MMM d, yyyy');
	if (start.getFullYear() !== end.getFullYear()) {
		return `${format(start, 'MMM d, yyyy')} – ${format(end, 'MMM d, yyyy')}`;
	}
	if (start.getMonth() !== end.getMonth()) {
		return `${format(start, 'MMM d')} – ${format(end, 'MMM d, yyyy')}`;
	}
	return `${format(start, 'MMM d')} – ${format(end, 'd, yyyy')}`;
}
